"use client";
import React, { useState, useCallback } from "react";
import "./index.css";

function isJSON(str: string): boolean {
  if (typeof str !== "string" || !str.trim()) return false;
  try {
    JSON.parse(str);
    return true;
  } catch {
    return false;
  }
}

export default function JSONViewer() {
  const [data, setData] = useState<object | null>(null);
  const [formattedData, setFormattedData] = useState("");
  const [copyLegend, setCopyLegend] = useState("Copy");
  const [hasStringified, setHasStringified] = useState(false);
  const [isValidJSON, setIsValidJSON] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const showError = (message: string) => {
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(""), 4000);
  };

  const beautifyJSON = useCallback(() => {
    setErrorMessage("");
    if (!isValidJSON) {
      showError("Invalid JSON — paste valid JSON first.");
      return;
    }
    if (data) {
      setFormattedData(JSON.stringify(data, null, 2));
      setHasStringified(false);
    } else if (formattedData.trim()) {
      try {
        const parsed = JSON.parse(formattedData);
        setData(parsed);
        setFormattedData(JSON.stringify(parsed, null, 2));
        setHasStringified(false);
      } catch {
        showError("Invalid JSON — cannot beautify.");
      }
    }
  }, [data, formattedData, isValidJSON]);

  const minifyJSON = useCallback(() => {
    setErrorMessage("");
    if (!isValidJSON && !formattedData.trim()) {
      showError("Invalid JSON — paste valid JSON first.");
      return;
    }
    try {
      const str = formattedData.trim();
      if (!str) return;
      const parsed = JSON.parse(str);
      setFormattedData(JSON.stringify(parsed));
      setData(parsed);
      setHasStringified(false);
    } catch {
      showError("Invalid JSON — cannot minify.");
    }
  }, [formattedData, isValidJSON]);

  const stringifyJSON = useCallback(() => {
    setErrorMessage("");
    if (!isValidJSON && !formattedData.trim()) {
      showError("Invalid JSON — paste valid JSON first.");
      return;
    }
    try {
      const str = formattedData.trim();
      if (!str) return;
      const parsed = JSON.parse(str);
      setFormattedData(JSON.stringify(JSON.stringify(parsed)));
      setHasStringified(true);
    } catch {
      showError("Invalid JSON — cannot stringify.");
    }
  }, [formattedData, isValidJSON]);

  const clearContent = useCallback(() => {
    setData(null);
    setFormattedData("");
    setHasStringified(false);
    setIsValidJSON(true);
    setErrorMessage("");
  }, []);

  const copyToClipboard = useCallback(async () => {
    if (!formattedData) return;
    try {
      await navigator.clipboard.writeText(formattedData);
      setCopyLegend("Copied!");
      setTimeout(() => setCopyLegend("Copy"), 2000);
    } catch {
      setCopyLegend("Copy failed");
      setTimeout(() => setCopyLegend("Copy"), 2000);
    }
  }, [formattedData]);

  const storeData = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setFormattedData(value);
    if (!value.trim()) {
      setData(null);
      setIsValidJSON(true);
      setHasStringified(false);
      return;
    }
    if (isJSON(value)) {
      try {
        setData(JSON.parse(value));
        setIsValidJSON(true);
      } catch {
        setData(null);
        setIsValidJSON(false);
      }
    } else {
      setData(null);
      setIsValidJSON(false);
    }
    setHasStringified(false);
  }, []);

  return (
    <div className="app min-h-screen min-v-screen p-8 bg-grey-lightest font-sans">
      <div className="row flex">
        <div className="col w-screen">
          <div className="box border rounded flex flex-col shadow bg-white">
            <div className="box__title bg-grey-lighter px-3 py-2 border-b">
              <div className="flex text-lg text-grey-darker font-medium justify-center">
                JSON Viewer
              </div>
              {errorMessage && (
                <div
                  className="text-center py-2 px-3 mt-2 rounded text-sm text-red-700 bg-red-50 border border-red-200"
                  role="alert"
                >
                  {errorMessage}
                </div>
              )}
              <div className="flex flex-wrap justify-center gap-2 mt-4 mb-4">
                <div className="button-borders">
                  <button
                    type="button"
                    className="primary-button"
                    title="Convert JSON into escaped string"
                    onClick={stringifyJSON}
                  >
                    Stringify
                  </button>
                </div>
                <div className="button-borders">
                  <button
                    type="button"
                    className="primary-button"
                    title="Minify the JSON — remove whitespace"
                    onClick={minifyJSON}
                  >
                    Minify
                  </button>
                </div>
                <div className="button-borders">
                  <button
                    type="button"
                    className="primary-button"
                    title="Format JSON with indentation"
                    onClick={beautifyJSON}
                  >
                    Beautify
                  </button>
                </div>
                <div className="button-borders">
                  <button
                    type="button"
                    className="primary-button"
                    title="Clear all content"
                    onClick={clearContent}
                  >
                    Clear
                  </button>
                </div>
                <div className="button-borders">
                  <button
                    type="button"
                    className={`primary-button ${
                      copyLegend === "Copied!"
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                    title="Copy JSON to clipboard"
                    disabled={copyLegend === "Copied!"}
                    onClick={copyToClipboard}
                  >
                    {copyLegend}
                  </button>
                </div>
              </div>
            </div>
            <textarea
              id="jsondata"
              placeholder="Paste JSON here (e.g. { &quot;key&quot;: &quot;value&quot; })"
              className={`text-grey-darkest flex-1 p-2 bg-transparent focus:outline-none font-mono resize-y min-h-[200px] ${
                !isValidJSON && formattedData.trim() ? "border-4 border-red-500" : ""
              }`}
              name="inputJSON"
              cols={30}
              rows={50}
              value={formattedData}
              onChange={storeData}
              aria-invalid={!isValidJSON && !!formattedData.trim()}
              aria-describedby={!isValidJSON && formattedData.trim() ? "json-error" : undefined}
            />
            {!isValidJSON && formattedData.trim() && (
              <p id="json-error" className="text-red-600 text-sm px-2 py-1" role="status">
                Invalid JSON — check syntax (missing comma, bracket, or quote).
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
